// Imperative half of the drill (drill.js is the pure half): attempt timers,
// mic lifecycle, record -> transcribe -> score, and self-playback. No Vue; the
// outside world comes in through the `host` adapter so tests can stub it.
//
// Invariants:
// 1. The mic is never open while audio plays (video or cue). On iOS an open
//    capture stream pins playback to the earpiece and ducks it. openMic()
//    awaits the cue gate from gateMic(), not just effect order.
// 2. Every path that abandons an attempt emits STOP_ALL_TIMERS, and
//    stopAllTimers() releases the mic.
// 3. `_attemptToken` is bumped on every start/abandon; endAttempt re-checks it
//    after each await so a late transcription never scores the wrong sentence.

import * as micStream from './micStream';
import { transcribe } from './asr';
import { scoreAttempt } from './scoring';
import { playPassSound, playEndSound } from './passSound';
import {
  SILENCE_MS,
  attemptCapMs,
  EMPTY_HINT_MS,
  REPLAY_HOLD_MS,
  PASS_THRESHOLD
} from './drill';

// Recordings stay in memory only: localStorage would fight the quota recovery
// of ezshadowTranscripts.v1 / ezshadowHistory.v1.
export const KEPT_RECORDINGS = 3;

// iOS picks the output route per element: after a capture, a plain Audio()
// plays from the earpiece while a <video> element gets the speaker. So iOS
// plays the recording through an off-screen <video> (must be attached, and not
// display:none, or WebKit declines to play it).
function createPlaybackElement (url) {
  if (typeof document === 'undefined' || !micStream.isIOS()) return new Audio(url);

  const el = document.createElement('video');
  el.setAttribute('playsinline', '');
  el.setAttribute('webkit-playsinline', '');
  el.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none';
  el.src = url;
  document.body.appendChild(el);
  return el;
}

function releasePlaybackElement (el) {
  try {
    el.pause();
  } catch (e) { /* already gone */ }
  if (el.parentNode) el.parentNode.removeChild(el);
}

export default class DrillController {
  /**
   * @param {Object} host getDrill, getSentence, getLang, getAdvanceDelayMs, send,
   *   setMicActive, setMicDenied, setRecognizedText, onAttemptText,
   *   onScoreResult, onRecordingUrl, onPlayingSelf, onSpeakDeadline(ms|null)
   *   (null when no attempt is being timed)
   */
  constructor (host) {
    this.host = host;

    this._capTimer = null;
    this._hintTimer = null;
    this._advanceTimer = null;
    this._replayHoldTimer = null;

    this._attemptToken = 0;
    this._micOpen = false;
    // Set by gateMic(), consumed by the next openMic().
    this._micGate = null;

    this._recordingUrls = [];
    this._lastRecording = null;
    this._selfAudio = null;
    this._selfPlayToken = 0;
  }

  // Video is already paused here (PAUSE_VIDEO precedes START_SPEAK_TIMERS).
  startSpeakTimers () {
    this._attemptToken++;
    this.host.onRecordingUrl(null);
    this.clearCapTimer();
    this.clearHintTimer();
    this.host.onSpeakDeadline(null);

    const capMs = this.host.getDrill().micStalled
      ? 0
      : attemptCapMs(this.host.getSentence());
    this._capMs = capMs;
    this._gateWaitMs = 0;

    // Cap timer and ring start only after the mic is actually open (post cue-gate
    // + getUserMedia). Starting them immediately caused two bugs: the ring drained
    // while the visualization was still gray (user couldn't tell the mic wasn't
    // ready), and the cap fired mid-speech because the timer consumed the cue wait.
    const token = this._attemptToken;
    // Return value intentionally unused in production; tests await it.
    const micReady = this.openMic().then(() => {
      if (token !== this._attemptToken || !this._micOpen) return;
      this._speakStartedAt = Date.now();
      if (capMs) {
        this._capTimer = setTimeout(() => { this.finishAttempt(); }, capMs);
      }
      this.host.onSpeakDeadline(capMs || null);
    });

    this._hintTimer = setTimeout(() => {
      if (!micStream.hasDetectedSpeech()) {
        this.host.send({ type: 'EMPTY_TIMEOUT' });
      }
    }, EMPTY_HINT_MS);

    return micReady;
  }

  stopSpeakTimers () {
    this.clearCapTimer();
    this.clearHintTimer();
    this.host.onSpeakDeadline(null);
    this.closeMic();
  }

  // VAD silence or the visible cap ran out: same "that's your take" chime and
  // submit. A ring tap calls endAttempt({ manual: true }) directly, no chime.
  finishAttempt () {
    playEndSound();
    this.endAttempt();
  }

  // openMic() no-ops while a stream is open, so the close must land first.
  resetAttempt () {
    return this.closeMic().then(() => {
      const drill = this.host.getDrill();
      if (drill.status === 'speak' && !drill.scoring) {
        this.startSpeakTimers();
      }
    });
  }

  startAdvanceTimer () {
    this.clearAdvanceTimer();
    this._advanceTimer = setTimeout(() => {
      this.host.send({ type: 'ADVANCE_TIMER_ELAPSED' });
    }, this.host.getAdvanceDelayMs());
  }

  stopAdvanceTimer () {
    this.clearAdvanceTimer();
  }

  startReplayHoldTimer () {
    this.clearReplayHoldTimer();
    this._replayHoldTimer = setTimeout(() => {
      this.host.send({ type: 'REPLAY_HOLD_ELAPSED' });
    }, REPLAY_HOLD_MS);
  }

  // Also releases the mic (invariant 2).
  stopAllTimers () {
    this._attemptToken++;
    // Defensive: never leave a stale cue gate for a later attempt (BUGFIX-003 §2a).
    this._micGate = null;
    this.clearCapTimer();
    this.clearHintTimer();
    this.clearAdvanceTimer();
    this.clearReplayHoldTimer();
    this.host.onSpeakDeadline(null);
    this.stopSelfPlayback();
    this.closeMic();
  }

  clearCapTimer () {
    if (this._capTimer) {
      clearTimeout(this._capTimer);
      this._capTimer = null;
    }
  }

  clearHintTimer () {
    if (this._hintTimer) {
      clearTimeout(this._hintTimer);
      this._hintTimer = null;
    }
  }

  clearAdvanceTimer () {
    if (this._advanceTimer) {
      clearTimeout(this._advanceTimer);
      this._advanceTimer = null;
    }
  }

  clearReplayHoldTimer () {
    if (this._replayHoldTimer) {
      clearTimeout(this._replayHoldTimer);
      this._replayHoldTimer = null;
    }
  }

  // Delays the next openMic() until the speak cue's promise settles, so the
  // mic never captures the tone's tail (BUGFIX-003 §2a, invariant 1).
  gateMic (promise) {
    this._micGate = promise;
  }

  openMic () {
    if (!micStream.isSupported()) {
      this.host.send({ type: 'MIC_DENIED' });
      return Promise.resolve();
    }
    if (this._micOpen) return Promise.resolve();
    this._micOpen = true;

    const token = this._attemptToken;
    const gate = this._micGate || Promise.resolve();
    this._micGate = null;
    const gateAt = Date.now();

    return gate.then(() => {
      this._gateWaitMs = Date.now() - gateAt; // ponytail: diagnostic only
      // Stale gate: the attempt was abandoned or restarted in place while the
      // cue played. Don't open a second stream, and don't touch _micOpen: a
      // restart has already set it true for the real, current attempt.
      if (token !== this._attemptToken) {
        return undefined;
      }
      return micStream.start({
        silenceMs: SILENCE_MS,
        onSilence: () => { this.finishAttempt(); }
      }).then(() => {
        this.host.setMicActive(true);
      }).catch((err) => {
        this._micOpen = false;
        console.warn('Microphone unavailable:', err);
        this.host.setMicDenied(true);
        this.host.send({ type: 'MIC_DENIED' });
      });
    });
  }

  // Idempotent, and returns the recording so endAttempt can score it.
  closeMic () {
    if (!this._micOpen) return Promise.resolve(null);
    this._micOpen = false;
    this.host.setMicActive(false);
    return micStream.stop().catch(() => null);
  }

  // See micStream.whenRouteReady: "mic closed" is not "safe to play" on iOS.
  whenRouteReady () {
    return micStream.whenRouteReady();
  }

  /**
   * @param {{manual?: boolean}} [options] manual (Done tap) always submits;
   *   VAD and the cap timer submit only if something was heard.
   */
  async endAttempt (options) {
    const drill = this.host.getDrill();
    if (drill.status !== 'speak' || drill.scoring) return;

    const manual = !!(options && options.manual);
    const token = this._attemptToken;
    const sentence = this.host.getSentence();

    // Read these BEFORE closing, which resets them.
    const heardSpeech = micStream.hasDetectedSpeech();
    // Weaker than heardSpeech (see micStream.hasSpeechEnergy): some devices
    // never sustain VAD_ARM_MS, and a cap expiry would discard every take.
    const heardAnything = heardSpeech || micStream.hasSpeechEnergy();

    // ponytail: diagnostic only (STORY-003/BUGFIX-005). used ~= cap means the
    // deadline cut the take; used < cap means VAD ended it.
    console.log('[drillController] attempt end: used=' + (Date.now() - this._speakStartedAt) +
      'ms cap=' + this._capMs +
      'ms sentence=' + Math.round(sentence ? (sentence.end - sentence.start) * 1000 : 0) +
      'ms cueGate=' + this._gateWaitMs +
      'ms heard=' + heardSpeech + ' energy=' + micStream.hasSpeechEnergy() +
      ' peak/thr=' + micStream.peakForTuning() + ' manual=' + manual);

    // Close first: finalizes the clip and frees the audio session before any
    // network wait, since feedback playback may start as soon as scoring lands.
    const clip = await this.closeMic();
    if (token !== this._attemptToken || this.host.getDrill().status !== 'speak') return;

    // Never upload silence: Whisper hallucinates on it ("Thank you.").
    if (!clip || !sentence || (!heardAnything && !manual)) {
      this.host.send({ type: 'ATTEMPT_ABORTED' });
      return;
    }

    this.setAttemptRecording(clip.blob);
    this.host.send({ type: 'ATTEMPT_SUBMITTED' });

    const lang = this.host.getLang();
    let text;
    try {
      text = await transcribe(clip.blob, lang);
    } catch (e) {
      if (token !== this._attemptToken) return;
      this.host.send({ type: 'ATTEMPT_ABORTED', code: (e && e.code) || 'UNAVAILABLE' });
      return;
    }

    const after = this.host.getDrill();
    if (token !== this._attemptToken || after.status !== 'speak' || !after.scoring) return;

    this.host.onAttemptText(text);
    this.host.setRecognizedText(text);

    if (!text) {
      this.host.send({ type: 'ATTEMPT_ABORTED' });
      return;
    }

    const result = scoreAttempt(sentence.text, text, lang);
    if (result.accuracy === null) {
      // Reference tokenized to nothing: skip rather than score null.
      this.host.onScoreResult(null);
      this.host.send({ type: 'NEXT_TAPPED' });
      return;
    }
    this.host.onScoreResult(result);
    if (result.accuracy >= PASS_THRESHOLD) playPassSound(result.accuracy);
    this.host.send({ type: 'ATTEMPT_SCORED', accuracy: result.accuracy });
  }

  setAttemptRecording (blob) {
    const url = URL.createObjectURL(blob);
    this._recordingUrls.push(url);
    while (this._recordingUrls.length > KEPT_RECORDINGS) {
      URL.revokeObjectURL(this._recordingUrls.shift());
    }
    // Bytes kept for "attach my voice" on a share; memory only, dropped on destroy.
    this._lastRecording = blob;
    this.host.onRecordingUrl(url);
  }

  getAttemptRecording () {
    return this._lastRecording;
  }

  /**
   * Waits for micStream.whenRouteReady(): on iOS, playback started before the
   * post-release route nudge finishes goes to the earpiece.
   */
  playSelf (url) {
    if (!url) return;
    this.stopSelfPlayback();

    const token = ++this._selfPlayToken;
    this.host.onPlayingSelf(true);
    // Don't let the advance countdown fire while the recording plays.
    this.clearAdvanceTimer();

    micStream.whenRouteReady().then(() => {
      if (token !== this._selfPlayToken) return;

      const audio = createPlaybackElement(url);
      this._selfAudio = audio;
      // BUGFIX-005 §1 diagnostic: which element branch ran, to tell a
      // misdetected isIOS() apart from a real route problem.
      console.log('[drillController] hear-yourself: element=' + audio.tagName + ' iOS=' + micStream.isIOS());

      const finish = () => {
        if (this._selfAudio !== audio) return;
        this._selfAudio = null;
        releasePlaybackElement(audio);
        this.host.onPlayingSelf(false);
        const drill = this.host.getDrill();
        if (drill.autoAdvancing || drill.autoRetrying) {
          this.startAdvanceTimer();
        }
      };
      audio.onended = finish;
      audio.onerror = finish;
      // play() predates promises in older Safari, and jsdom returns nothing.
      const started = audio.play();
      if (started && started.catch) {
        started.catch((err) => {
          console.log('[drillController] hear-yourself: play() rejected: ' + (err && err.message));
          finish();
        });
      }
    });
  }

  stopSelfPlayback () {
    this._selfPlayToken++;
    if (this._selfAudio) {
      releasePlaybackElement(this._selfAudio);
      this._selfAudio = null;
    }
    this.host.onPlayingSelf(false);
  }

  releaseRecordings () {
    this._recordingUrls.forEach(url => URL.revokeObjectURL(url));
    this._recordingUrls = [];
    this._lastRecording = null;
    this.host.onRecordingUrl(null);
  }

  /** Leaving the screen must never strand an open capture stream. */
  destroy () {
    this.stopAllTimers();
    this.stopSelfPlayback();
    this.releaseRecordings();
  }
}
